import { z } from "zod";

/** Klanten, locaties en contactpersonen: de laag tussen organisatie en gebouw (docs/CLIENTS.md). */

export const CLIENT_CONTACT_ROLES = ["owner", "financial", "technical", "other"] as const;
export type ClientContactRole = (typeof CLIENT_CONTACT_ROLES)[number];
export const CLIENT_CONTACT_ROLE_LABELS: Record<ClientContactRole, string> = {
  owner: "Eigenaar",
  financial: "Financieel",
  technical: "Technisch",
  other: "Overig",
};

/** Toegang tot een klant: alle gebouwen, of alleen gekozen locaties en gebouwen. */
export const CLIENT_ACCESS_SCOPES = ["all_buildings", "selected"] as const;
export type ClientAccessScope = (typeof CLIENT_ACCESS_SCOPES)[number];

/** Naam van de systeemklant waar gebouwen zonder indeling onder staan. */
export const UNASSIGNED_CLIENT_NAME = "Nog in te delen";

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

/** Voorvoegsel voor nieuwe gebouwcodes van een klant, bijv. `MOS`. */
export const clientCodePrefixSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{2,5}$/, "Gebruik 2 tot 5 letters of cijfers");

const clientFields = z.object({
  name: trimmed(120).min(1, "Vul een naam in"),
  shortName: optionalText(40),
  codePrefix: clientCodePrefixSchema.nullish().transform((v) => v ?? null),
  reference: optionalText(60),
  address: optionalText(200),
  postalCode: optionalText(10),
  city: optionalText(80),
  /** Indexpercentage voor het MJOP; leeg = dat van de organisatie. */
  mjopIndexPct: z.number().min(-20).max(50).nullish(),
});
export const clientCreateSchema = clientFields.extend({ id: z.uuid().optional() });
export const clientUpdateSchema = clientFields.partial();
export type ClientCreateBody = z.input<typeof clientCreateSchema>;
export type ClientUpdateBody = z.input<typeof clientUpdateSchema>;

const siteFields = z.object({
  name: trimmed(120).min(1, "Vul een naam in"),
  address: optionalText(200),
  postalCode: optionalText(10),
  city: optionalText(80),
});
export const siteCreateSchema = siteFields.extend({ id: z.uuid().optional() });
export const siteUpdateSchema = siteFields.partial();
export type SiteCreateBody = z.input<typeof siteCreateSchema>;

const contactFields = z.object({
  name: trimmed(120).min(1, "Vul een naam in"),
  jobTitle: optionalText(80),
  email: z
    .email("Vul een geldig e-mailadres in")
    .trim()
    .toLowerCase()
    .nullish()
    .or(z.literal("").transform(() => null))
    .transform((v) => v ?? null),
  phone: optionalText(30),
  role: z.enum(CLIENT_CONTACT_ROLES).default("other"),
  isPrimary: z.boolean().default(false),
  notes: optionalText(1000),
});
export const contactCreateSchema = contactFields.extend({ id: z.uuid().optional() });
export const contactUpdateSchema = contactFields.partial();
export type ContactCreateBody = z.input<typeof contactCreateSchema>;

export interface SiteDto {
  id: string;
  clientId: string;
  name: string;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  buildingCount: number;
}

export interface ClientContactDto {
  id: string;
  clientId: string;
  name: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  role: ClientContactRole;
  isPrimary: boolean;
  notes: string | null;
}

export interface ClientDto {
  id: string;
  name: string;
  shortName: string | null;
  codePrefix: string | null;
  reference: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  mjopIndexPct: number | null;
  /** De systeemklant "Nog in te delen": niet te hernoemen, archiveren of verwijderen. */
  isUnassigned: boolean;
  archivedAt: string | null;
  /** Actieve gebouwen die de gebruiker van deze klant ziet. */
  buildingCount: number;
}

export interface ClientDetailDto extends ClientDto {
  sites: SiteDto[];
  contacts: ClientContactDto[];
}

const isoDate = z.iso.date("Kies een geldige datum");

/** Gebouwen naar een andere klant of locatie verplaatsen, los of in bulk (docs/CLIENTS.md §5). */
export const buildingMoveSchema = z.object({
  buildingIds: z.array(z.uuid()).min(1, "Kies minstens één gebouw").max(500),
  clientId: z.uuid(),
  siteId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  /** Ingangsdatum, vandaag of eerder. Leeg = vandaag. */
  effectiveDate: isoDate.nullish().transform((v) => v ?? null),
  reason: optionalText(300),
});
export type BuildingMoveBody = z.input<typeof buildingMoveSchema>;

export interface BuildingMoveResult {
  /** Id van deze verplaatsing, voor "Ongedaan maken". Null als er niets veranderde. */
  moveId: string | null;
  moved: number;
  /** Ongewijzigd: stond al bij deze klant en locatie. */
  unchanged: number;
  /** Tot wanneer "Ongedaan maken" kan. */
  undoUntil: string | null;
}

/** Hoe lang na een verplaatsing "Ongedaan maken" kan (de app toont de knop 10 seconden). */
export const MOVE_UNDO_SECONDS = 120;

export interface BuildingClientHistoryDto {
  id: string;
  client: { id: string; name: string; isUnassigned: boolean };
  site: { id: string; name: string } | null;
  fromDate: string;
  toDate: string | null;
  reason: string | null;
  movedBy: string | null;
}

/** Toegang van een medewerker tot één klant (docs/CLIENTS.md §4.8, §7.5). */
export const memberClientAccessSchema = z.object({
  clientId: z.uuid(),
  scope: z.enum(CLIENT_ACCESS_SCOPES),
  siteIds: z.array(z.uuid()).max(500).default([]),
  buildingIds: z.array(z.uuid()).max(2000).default([]),
});
export const memberAccessBodySchema = z.object({ clients: z.array(memberClientAccessSchema).max(500) });
export type MemberClientAccess = z.output<typeof memberClientAccessSchema>;
export type MemberAccessBody = z.input<typeof memberAccessBodySchema>;
